import {AnimationItem} from 'lottie-web/build/player/lottie_light';
import {MouseEventHandler, ReactNode, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import LIKE_EFFECT_LIGHT from '../../../public/static/viewer/like-effect-light.json';
import LIKE_EFFECT from '../../../public/static/viewer/like-effect.json';
import LIKE_FIRST_LIGHT from '../../../public/static/viewer/like-first-light.json';
import LIKE_FIRST from '../../../public/static/viewer/like-first.json';
import LIKE_MORE_LIGHT from '../../../public/static/viewer/like-more-light.json';
import LIKE_MORE from '../../../public/static/viewer/like-more.json';
import Lottie from '../../common/component/Lottie';
import Text from '../../common/component/Text';
import {pToast} from '../../common/container/PToast';
import {showAuthModal} from '../../common/function';
import useEffectOnChange from '../../common/hook/useEffectOnChange';
import useLogin from '../../common/hook/useLogin';
import {IFetchStatus, ITheme} from '../../common/type';
import {formatNumber} from '../../common/util/number';

interface IProps {
  className?: string;
  theme: ITheme;
  totalLikeCount: number;
  myLikeCount: number;
  fetchStatus: IFetchStatus;
  onClickThumb: LikeInfoHandlerV4;
  bottomElement?: ReactNode;
}

export type ILikeTypeV4 = 'good' | 'nice' | 'great' | 'best' | 'perfect';

export type LikeInfoHandlerV4 = (params: {
  myLikeCount: number;
  rollbackCount: number;
  trackingValue?: ILikeTypeV4;
}) => void;

// TODO: ViewerEnd 컴포넌트에 적용하자
export default function LikeInfoV4({
  className,
  theme,
  totalLikeCount,
  myLikeCount,
  fetchStatus,
  onClickThumb,
  bottomElement,
}: IProps) {
  const {t} = useTranslation();
  const {isLogin} = useLogin();
  const thumbFirstRef = useRef<AnimationItem>();
  const thumbMoreRef = useRef<AnimationItem>();
  const effectRef = useRef<AnimationItem>();
  const rollbackCountRef = useRef(myLikeCount);
  const hasTotalLikeCount = totalLikeCount > 0;
  const [showMoreThumb, setShowMoreThumb] = useState(myLikeCount > 0);

  const handleClickThumb: MouseEventHandler = e => {
    e.stopPropagation();

    if (!isLogin) {
      showAuthModal();
      return;
    }
    if (myLikeCount >= MAX_LIKE_COUNT) {
      pToast({
        desc: t('viewer_end_like_snackbar'),
        variant: 'black',
      });
      return;
    }

    const thumbAni =
      myLikeCount === 0 ? thumbFirstRef.current : thumbMoreRef.current;
    thumbAni?.goToAndPlay(0);
    effectRef.current?.goToAndPlay(0);
    setShowMoreThumb(myLikeCount > 0);
    onClickThumb({
      myLikeCount,
      rollbackCount: rollbackCountRef.current,
      trackingValue: LIKE_VALUE_LIST[myLikeCount],
    });
  };

  const handleEffectComplete = () => {
    effectRef.current?.goToAndStop(0);
  };

  useEffectOnChange(() => {
    if (fetchStatus === 'success') {
      rollbackCountRef.current = myLikeCount;
    }
  }, fetchStatus);

  return (
    <div
      className={cn(
        'flex justify-center',
        'relative overflow-hidden h-[184px]',
        className
      )}
    >
      <Text
        className={cn('absolute top-42', {
          's21-bold-white': hasTotalLikeCount,
          's14-semibold-white': !hasTotalLikeCount,
          '!text-black': theme === 'light',
        })}
      >
        {hasTotalLikeCount
          ? formatNumber(totalLikeCount)
          : t('viewer_end_like_first')}
      </Text>
      <Lottie
        className="absolute w-60 h-30 top-20"
        lottieRef={effectRef}
        animationData={ANIMATION_DATA_MAP[theme].likeEffect}
        autoplay={false}
        onComplete={handleEffectComplete}
      />
      <Lottie
        className={cn('absolute w-80 h-80 top-72', {
          hidden: showMoreThumb,
        })}
        lottieRef={thumbFirstRef}
        animationData={ANIMATION_DATA_MAP[theme].thumbFirst}
        autoplay={false}
        onClick={handleClickThumb}
      />
      <Lottie
        className={cn('absolute w-80 h-80 top-72', {
          hidden: !showMoreThumb,
        })}
        lottieRef={thumbMoreRef}
        animationData={ANIMATION_DATA_MAP[theme].thumbMore}
        autoplay={false}
        onClick={handleClickThumb}
      />
      {bottomElement}
    </div>
  );
}

const MAX_LIKE_COUNT = 5;

const ANIMATION_DATA_MAP = {
  dark: {
    likeEffect: LIKE_EFFECT,
    thumbFirst: LIKE_FIRST,
    thumbMore: LIKE_MORE,
  },
  light: {
    likeEffect: LIKE_EFFECT_LIGHT,
    thumbFirst: LIKE_FIRST_LIGHT,
    thumbMore: LIKE_MORE_LIGHT,
  },
};

export const LIKE_VALUE_LIST: ILikeTypeV4[] = [
  'good',
  'nice',
  'great',
  'best',
  'perfect',
];
